/**
 * Workshop tree-type icons + brand colors, keyed by tree-species name.
 *
 * MIRRORED from the booking app's `app/lib/design-tokens.ts`
 * (shopify-app/bonsaibar-workshop-pages) — `@bonsai/shared` is unwired, so the
 * repo convention is per-package mirroring. KEEP THESE TWO MAPS IN SYNC with
 * that file when species art/colors change. Only a subset of species have icon
 * art today; the rest fall back to a solid brand-colored tile.
 */
import dwarfJadeIcon from '../assets/workshop-icons/DwarfJade_Icon01@2x.png';
import dwarfUmbrellaIcon from '../assets/workshop-icons/DwarfUmbrella_Icon01@2x.png';
import lavenderStarFlowerIcon from '../assets/workshop-icons/LavenderStarFlower_Icon01@2x.png';
import repottingIcon from '../assets/workshop-icons/Repotting_Icon01@2x.png';
import variegatedDwarfJadeIcon from '../assets/workshop-icons/VariegatedDwarfJade_Icon01@2x.png';

export interface WorkshopTypeColors {
  bg: string;
  text: string;
  mid: string;
}

const WORKSHOP_TYPE_COLORS: Record<string, WorkshopTypeColors> = {
  'Dwarf Jade': { bg: '#2D6B2D', text: '#FFFFFF', mid: '#8FBF8F' },
  'Dwarf Umbrella': { bg: '#2B4F6E', text: '#FFFFFF', mid: '#87CEEB' },
  Juniper: { bg: '#2D5A1B', text: '#FFFFFF', mid: '#5A8A3A' },
  'Parrots Beak': { bg: '#8B4513', text: '#FFFFFF', mid: '#A0694B' },
  Repotting: { bg: '#6B4226', text: '#FFFFFF', mid: '#A67B5B' },
  'Hinoki Cypress': { bg: '#3E7A3E', text: '#FFFFFF', mid: '#6DB56D' },
  'Alberta Spruce': { bg: '#1B4D3E', text: '#FFFFFF', mid: '#4A8B6F' },
  Ficus: { bg: '#228B22', text: '#FFFFFF', mid: '#4CAF50' },
  'Mammoth Umbrella': { bg: '#1A5276', text: '#FFFFFF', mid: '#5DADE2' },
  'Mammoth Jade': { bg: '#1E6B1E', text: '#FFFFFF', mid: '#6BBF6B' },
  Mammoth: { bg: '#4A148C', text: '#FFFFFF', mid: '#7B1FA2' },
  'Big Jade': { bg: '#3B6B3B', text: '#FFFFFF', mid: '#7AAF7A' },
  'Lavender Star Flower': { bg: '#7B2D6B', text: '#FFFFFF', mid: '#D4A0C9' },
  'Variegated Dwarf Jade': { bg: '#5A7A2D', text: '#FFFFFF', mid: '#C9D98C' },
};

const FALLBACK_COLORS: WorkshopTypeColors = { bg: '#5D675D', text: '#FFFFFF', mid: '#96A096' };

const WORKSHOP_TYPE_ICONS: Record<string, string | null> = {
  'Dwarf Jade': dwarfJadeIcon,
  'Dwarf Umbrella': dwarfUmbrellaIcon,
  Repotting: repottingIcon,
  'Lavender Star Flower': lavenderStarFlowerIcon,
  'Variegated Dwarf Jade': variegatedDwarfJadeIcon,
};

/** Brand colors for a tree species, or a neutral fallback. */
export function getWorkshopTypeColors(name: string | null): WorkshopTypeColors {
  if (!name) return FALLBACK_COLORS;
  return WORKSHOP_TYPE_COLORS[name] ?? FALLBACK_COLORS;
}

/** The tree-type icon asset URL for a species, or null when there's no art. */
export function getWorkshopTypeIcon(name: string | null): string | null {
  if (!name) return null;
  return WORKSHOP_TYPE_ICONS[name] ?? null;
}
